import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft,
  Calendar,
  HelpCircle,
  Bell,
  MapPin,
  Music,
  User,
  FileText,
  CalendarPlus,
  Shield,
  Key,
  MessageCircle,
  LayoutGrid,
  Sparkles,
  Repeat,
  Trophy,
  Users,
  MessageSquare,
} from "lucide-react";

const PlayerGuide = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: "Signing up with a Band Code",
      icon: <Key className="w-5 h-5" />,
      color: "text-amber-400",
      badge: "New",
      steps: [
        "On the login screen, click 'Sign up'",
        "Choose 'Join with Band Code' and type the 6-character code from your admin",
        "Once the band is found, a 'Your Section' dropdown will appear — selecting a section is now required",
        "Pick the section you play in (your admin can change this later if needed)",
        "Fill in your name, email and password and click 'Join Band & Create Account'"
      ]
    },
    {
      title: "Responding to Events",
      icon: <Calendar className="w-5 h-5" />,
      color: "text-blue-400",
      steps: [
        "Go to Events from the sidebar",
        "Click any event to see details",
        "Choose Attending (green), Can't Attend (red) or Maybe (yellow)",
        "Your response is saved automatically — no need to hit Save"
      ]
    },
    {
      title: "Leaving a Note with Your Response",
      icon: <MessageCircle className="w-5 h-5" />,
      color: "text-emerald-400",
      badge: "New",
      steps: [
        "Open the event details page",
        "Next to your RSVP buttons, type an optional note (up to 300 characters)",
        "Examples: 'Running 10 min late', 'Can only stay till 8pm', 'Back from holiday so may be rusty'",
        "Your note is visible to admins alongside your response",
        "Edit or clear the note any time — it syncs on save"
      ]
    },
    {
      title: "Understanding Event Status",
      icon: <HelpCircle className="w-5 h-5" />,
      color: "text-yellow-400",
      steps: [
        "Confirmed (green) — event is definitely happening",
        "Provisional (yellow) — tentative, may change",
        "Cancelled (red) — event is off, title shown with strikethrough"
      ]
    },
    {
      title: "Finding the Event Location",
      icon: <MapPin className="w-5 h-5" />,
      color: "text-green-400",
      steps: [
        "Open the event details",
        "Click the location (it has a link icon)",
        "Google Maps opens with directions from wherever you are"
      ]
    },
    {
      title: "Downloading Event Files",
      icon: <FileText className="w-5 h-5" />,
      color: "text-purple-400",
      steps: [
        "Open the event details",
        "Click the 'Files' tab",
        "Click the download icon next to any file",
        "Sheet music, concert programmes and logistics docs live here"
      ]
    },
    {
      title: "Syncing Events to Your Calendar",
      icon: <CalendarPlus className="w-5 h-5" />,
      color: "text-orange-400",
      steps: [
        "Go to Profile from the sidebar",
        "Scroll to 'Calendar Sync' and click 'Generate Calendar Link'",
        "Use 'Add to Google Calendar' or copy the URL into Apple Calendar / Outlook",
        "Only events you've said Yes or Maybe to will sync — no clutter"
      ]
    },
    {
      title: "Being in Multiple Bands",
      icon: <Repeat className="w-5 h-5" />,
      color: "text-fuchsia-400",
      badge: "New",
      steps: [
        "You can belong to several bands at once (e.g. a main band + a depping group)",
        "Use the Band Switcher in the sidebar to change the active band",
        "Click 'Join another band' in the switcher and enter that band's 6-character code",
        "Your Dashboard shows a 'My Schedule' card with the next 3 events across every band you're in",
        "Open /calendar/all (All-Bands Calendar) for a colour-coded combined calendar"
      ]
    },
    {
      title: "Push Notifications",
      icon: <Bell className="w-5 h-5" />,
      color: "text-pink-400",
      steps: [
        "Go to Profile from the sidebar",
        "Find the 'Push Notifications' toggle",
        "Turn it on to receive instant alerts on your phone or laptop",
        "You'll get pinged for new events and reminders"
      ]
    },
    {
      title: "Viewing Your Sections",
      icon: <Music className="w-5 h-5" />,
      color: "text-cyan-400",
      steps: [
        "Go to Sections from the sidebar",
        "Find your section in the list",
        "Click to expand and see the other players in it",
        "If you play more than one instrument, your profile can be in multiple sections — ask your admin to add extras"
      ]
    },
    {
      title: "Changing Your Password",
      icon: <Shield className="w-5 h-5" />,
      color: "text-red-400",
      steps: [
        "Go to Profile from the sidebar",
        "Find the 'Change Password' section",
        "Enter your current password, then your new one twice",
        "Click 'Change Password'"
      ]
    },
    {
      title: "Your Dashboard at a Glance",
      icon: <LayoutGrid className="w-5 h-5" />,
      color: "text-sky-400",
      steps: [
        "Shows the next few events you need to RSVP to",
        "Trial or subscription banners appear at the top if your admin needs to pay",
        "If you're in 2+ bands, the 'My Schedule' widget shows upcoming events across all of them",
        "Tap an event card to jump straight to details and RSVP"
      ]
    },
    // iter-106 — additions for newly shipped player-facing features.
    {
      title: "Achievements & Badges",
      icon: <Trophy className="w-5 h-5" />,
      color: "text-amber-300",
      badge: "New",
      steps: [
        "Click 'Achievements' in the sidebar to see every badge you can earn",
        "Badges unlock automatically from your attendance — no clicks needed",
        "Examples: First Note, Five Strong, Perfect Month, Concert Hero, Section Stalwart (50 events), Two-Year Club (24 months running)",
        "When you earn a new badge you'll see a 🎉 toast on the Dashboard and a glowing 'NEW' ring on the chip",
        "The card on the Dashboard shows your most recent unlocks and your next milestone"
      ]
    },
    {
      title: "Managing My Memberships",
      icon: <Users className="w-5 h-5" />,
      color: "text-fuchsia-300",
      badge: "New",
      steps: [
        "Click 'My Memberships' in the sidebar to see every band you belong to",
        "Each card shows your role (Admin / Member / Dep), the band's status, and your last activity in that band",
        "Click 'Leave band' on any band you no longer need to be part of",
        "Your currently-active band can't be left — switch bands first (band switcher in the sidebar top)",
        "If you're the last admin of a band, leaving is blocked until another admin is appointed"
      ]
    },
    {
      title: "Chat Rooms & Unread Badges",
      icon: <MessageSquare className="w-5 h-5" />,
      color: "text-emerald-300",
      badge: "Updated",
      steps: [
        "Each band has a General chat plus auto-generated section rooms (Cornets, Trombones, etc.)",
        "You only see rooms for sections you play in — even if your band has more",
        "The number next to 'Chat' in the sidebar is your total unread across every room you can see",
        "Tap the row to open the room, scroll messages, send inline images and reactions",
        "Long-press (or right-click) any message you've sent to edit or delete it",
        "Hit 'Mark all read' to clear the badge when you're caught up"
      ]
    },
    {
      title: "Public Concert Widget",
      icon: <Calendar className="w-5 h-5" />,
      color: "text-cyan-300",
      badge: "Updated",
      steps: [
        "Your band's public website may embed an upcoming-concerts widget powered by Vivo",
        "Times now display in UK local time automatically — handles GMT/BST so audiences see the correct hour",
        "Only confirmed concerts appear publicly (provisional and private events stay hidden)",
        "If your admin sets an event to 'private', the widget shows only the date — title and location are hidden"
      ]
    }
  ];

  return (
    <div className="space-y-6" data-testid="player-guide">
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
            <User className="w-8 h-8 text-primary" />
            Player Guide
          </h1>
          <p className="text-muted-foreground mt-1">
            Quick guides to help you get the most out of Vivo
          </p>
        </div>
      </div>

      <Card className="glass-card border-primary/20">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <strong className="text-foreground">What&apos;s new:</strong> signup now asks for your section, you can leave a note with your RSVP, and your dashboard shows a combined &ldquo;My Schedule&rdquo; if you&apos;re in more than one band.
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {sections.map((section, index) => (
          <Card key={index} className="glass-card" data-testid={`player-guide-card-${index}`}>
            <CardHeader className="pb-3">
              <CardTitle className={`font-heading text-lg flex items-center gap-2 ${section.color}`}>
                {section.icon}
                {section.title}
                {section.badge && (
                  <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/15 text-primary">
                    {section.badge}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-2">
                {section.steps.map((step, stepIndex) => (
                  <li key={stepIndex} className="flex gap-3 text-sm">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center font-bold">
                      {stepIndex + 1}
                    </span>
                    <span className="text-muted-foreground">{step}</span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="glass-card border-primary/20">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <HelpCircle className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-semibold mb-1">Need More Help?</h3>
              <p className="text-sm text-muted-foreground">
                Contact your band admin if you have questions or need assistance with anything not covered here.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlayerGuide;
